// =====================================================================
// The mutual-silence gate — pure state + decision (plans/silence-cutoff/plan.md).
//
// A voicemail that slips past AMD costs paid SDR time until somebody closes it.
// The one signal that separates "dead voicemail on screen" from a live call is
// MUTUAL silence: the lead is silent during every SDR pitch (a lead-only rule
// fires into live conversations at 0.79 catches per interruption; both-sides
// scores 12.3), so the gate fires only when NEITHER side has made a sound for
// `quietMs` and the card has been up at least `floorMs`.
//
// What firing MEANS is the caller's business, by mode: `off` nothing, `on` the
// full behaviour — the "still on this call?" banner, a countdown, and a cut on
// expiry. One switch, per the plan's ground rules.
//
// One fire per call, deliberately: the analysed prize is the first firing (the
// gate fires the instant it is permitted to — fire-time p50 sits exactly on the
// floor), and a re-arming gate turns one judgement call by the SDR into a nag
// loop. Serial Continue-clickers are visible in the per-SDR stayed/cut counts.
//
// Same contract as audio-liveness.ts: pure and timer-free, the caller owns the
// clock and the audibility thresholds (remoteAudible/localAudible from
// voicemail.ts — shared so the two features cannot drift on what "silent" means),
// and the blip mute needs no special-casing here because the SDK's `output` keeps
// reporting the lead's real level while playback is muted.
// =====================================================================

// The per-card config the core stamps on `lead_assigned.silenceGate` (see
// SilenceGateConfig in cockpit/protocol.ts). Held as data, not build-time env, so
// a console flip lands on the next call with no deploy.
export interface SilenceGateConfig {
  mode: string;
  floorMs: number;
  quietMs: number;
  // The lead-only tier's window; null = tier off (an older core omits it).
  leadQuietMs: number | null;
  graceMs: number;
  remoteMax: number;
  localMin: number;
}

// The gate's two tiers, and why both exist:
//   'mutual'    — BOTH sides quiet ≥ quietMs. Fast (fires at the floor) and never
//                 interrupts a pitch, but an SDR chatting over an open mic keeps
//                 resetting it — the most disengaged SDR blocks it.
//   'lead_only' — the LEAD alone silent ≥ leadQuietMs, regardless of SDR audio.
//                 Slower (30s+; at short windows it fires into every pitch, since
//                 a listening lead is a silent lead) but ungameable: nothing the
//                 SDR does resets it.
// Whichever condition crosses first fires; the receipt carries which, so the
// live data scores both rules on the same calls.
export type SilenceRule = 'mutual' | 'lead_only';

export type SilenceGateMode = 'off' | 'on';

// The mode as the SPA understands it. Anything unrecognised (a newer core's
// future mode, a null card field from an older core) degrades to 'off' — the gate
// must fail toward doing nothing.
export function gateMode(cfg: Pick<SilenceGateConfig, 'mode'> | null | undefined): SilenceGateMode {
  return cfg?.mode === 'on' ? 'on' : 'off';
}

export interface SilenceGateState {
  // When the card appeared, in the caller's clock. The floor measures from HERE —
  // card time is the SDR cost being bought back (voicemails.md: card_sent→ended).
  cardShownAt: number | null;
  // Start of the current mutual-quiet run, or null while either side is audible.
  // Conservatively the first QUIET SAMPLE's time, not the last audible one: with
  // the SDK's ~50ms cadence the difference is noise, and it can only delay a fire.
  quietSince: number | null;
  // Start of the current LEAD-quiet run — reset only by the lead's own sound,
  // never the SDR's. The lead-only tier's clock.
  leadQuietSince: number | null;
  // When the gate fired. Latched — this is what makes the gate one-shot.
  firedAt: number | null;
  // Which tier fired. The dismissal semantics depend on it: a lead-only fire is
  // dismissed by the LEAD making a sound, never by the SDR's own noise (their
  // noise is the thing the tier exists to see through).
  firedRule: SilenceRule | null;
  // When the raised prompt was dismissed (any reason). Latched.
  dismissedAt: number | null;
  // The observed quiet run at the moment of firing — a receipt field that exists
  // only at that moment, so it is captured here or nowhere.
  quietMsAtFire: number | null;
}

export function newSilenceGate(cardShownAt: number | null = null): SilenceGateState {
  return {
    cardShownAt,
    quietSince: null,
    leadQuietSince: null,
    firedAt: null,
    firedRule: null,
    dismissedAt: null,
    quietMsAtFire: null,
  };
}

export interface SilenceSample {
  // Far-side level cleared voicemail.ts's remote threshold this sample.
  remoteAudible: boolean;
  // Our mic cleared the local threshold this sample.
  localAudible: boolean;
  now: number;
}

// Fold one volume sample in. Kept deliberately dumb: an audible sample restarts
// the clocks it can reach — the mutual clock resets on EITHER side's sound, the
// lead clock only on the lead's.
export function observeSilence(state: SilenceGateState, sample: SilenceSample): void {
  if (sample.remoteAudible || sample.localAudible) {
    state.quietSince = null;
  } else if (state.quietSince == null) {
    state.quietSince = sample.now;
  }
  if (sample.remoteAudible) {
    state.leadQuietSince = null;
  } else if (state.leadQuietSince == null) {
    state.leadQuietSince = sample.now;
  }
}

// The current mutual-quiet run, ms.
export function quietFor(state: SilenceGateState, now: number): number {
  return state.quietSince == null ? 0 : Math.max(0, now - state.quietSince);
}

// The current lead-quiet run, ms.
export function leadQuietFor(state: SilenceGateState, now: number): number {
  return state.leadQuietSince == null ? 0 : Math.max(0, now - state.leadQuietSince);
}

// Card age, ms. Zero until a card is actually up — no card, no gate.
export function cardAge(state: SilenceGateState, now: number): number {
  return state.cardShownAt == null ? 0 : Math.max(0, now - state.cardShownAt);
}

// The rule: which tier (if any) crosses its threshold at `now`. Mode-independent
// on purpose (the rule is one thing, what firing does is the caller's); evaluated
// per ~50ms sample, so whichever condition crosses FIRST is the tier that fires —
// mutual is checked first only to break the tie when both cross on the same sample.
export function shouldFire(
  state: SilenceGateState,
  cfg: SilenceGateConfig,
  now: number,
): SilenceRule | null {
  if (state.firedAt != null || state.cardShownAt == null) return null;
  if (cardAge(state, now) < cfg.floorMs) return null;
  if (quietFor(state, now) >= cfg.quietMs) return 'mutual';
  if (cfg.leadQuietMs != null && leadQuietFor(state, now) >= cfg.leadQuietMs) return 'lead_only';
  return null;
}

// Latch the fire and capture the receipt fields. `quietMsAtFire` is the FIRING
// tier's observed run — the mutual run for 'mutual', the lead run for
// 'lead_only' — so the receipt's number always describes the rule that acted.
export function markFired(state: SilenceGateState, now: number, rule: SilenceRule): void {
  if (state.firedAt != null) return;
  state.firedAt = now;
  state.firedRule = rule;
  state.quietMsAtFire = rule === 'mutual' ? quietFor(state, now) : leadQuietFor(state, now);
}

// A raised prompt is one that fired and has not been dismissed.
export function isRaised(state: SilenceGateState): boolean {
  return state.firedAt != null && state.dismissedAt == null;
}

// Latch the dismissal (Continue click, a side making a sound, a disposition, or
// the cut itself). The gate never re-arms after this — see the header.
export function markDismissed(state: SilenceGateState, now: number): void {
  if (state.firedAt == null || state.dismissedAt != null) return;
  state.dismissedAt = now;
}

// Ms from fire to dismissal — the `reaction_ms` receipt on silence_gate_stayed.
export function reactionMs(state: SilenceGateState): number | null {
  if (state.firedAt == null || state.dismissedAt == null) return null;
  return Math.max(0, Math.round(state.dismissedAt - state.firedAt));
}
